import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from 'convex/react';
import { AlertOctagon, AlertTriangle, Info, X } from 'lucide-react';
import { Callout } from '@bill-alps/ui';
import { api } from '@bill-alps/backend/convex/_generated/api';
import {
  bannerText,
  selectVisibleBanners,
  type BannerSeverity,
} from '@bill-alps/backend/features/maintenanceBanners/selection';
import { useLanguage, useTranslation } from '../../i18n/useTranslation';
import {
  dismissBanner,
  getDismissedBanners,
} from './maintenance-banner-dismissals';

/**
 * How often the client re-checks whether a banner has started or ended. The
 * query hands over live AND upcoming banners because a Convex query re-runs on
 * database changes, not on the passage of time — this ticker is the clock.
 * 30s is the granularity of "it appeared on schedule" without being a spin.
 */
const TICK_MS = 30_000;

const SEVERITY_STYLES: Record<
  BannerSeverity,
  { className: string; Icon: typeof Info }
> = {
  info: { className: 'bg-muted', Icon: Info },
  warning: { className: 'bg-riso-amber/15', Icon: AlertTriangle },
  critical: { className: 'bg-destructive/15', Icon: AlertOctagon },
};

/**
 * Admin-authored maintenance / announcement bars, stacked most severe first.
 *
 * Mounted by `<GlobalBanners>` at the root of the app, so it covers the
 * signed-in shell, the sign-in screens and the employee portal alike — the
 * read query is anonymous for exactly that reason.
 *
 * Each bar is dismissible for the rest of the browser session
 * (`lib/maintenance-banner-dismissals.ts`), independently of the others.
 */
export function MaintenanceBanners() {
  const { t } = useTranslation('common');
  const { language } = useLanguage();
  const { pathname } = useLocation();
  const banners = useQuery(api.features.maintenanceBanners.banners.listActive);

  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState<string[]>(getDismissedBanners);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  if (!banners) return null;

  const visible = selectVisibleBanners(banners, pathname, now).filter(
    (banner) => !dismissed.includes(banner._id),
  );
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map((banner) => {
        const { className, Icon } = SEVERITY_STYLES[banner.severity];
        if (banner.severity === 'info') {
          return (
            <Callout
              key={banner._id}
              variant="info"
              size="sm"
              role="status"
              className="m-2"
              aria-label={t('maintenanceBanner.severity.info')}
              actions={
                <button
                  type="button"
                  aria-label={t('maintenanceBanner.dismiss')}
                  onClick={() => setDismissed(dismissBanner(banner._id))}
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" />
                </button>
              }
            >
              {bannerText(banner.message, language)}
            </Callout>
          );
        }
        return (
          <div
            key={banner._id}
            role="status"
            className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b-2 border-border px-4 py-2 text-sm ${className}`}
          >
            <span className="flex items-center gap-2 font-medium text-foreground">
              <Icon
                className="h-4 w-4 shrink-0"
                aria-label={t(`maintenanceBanner.severity.${banner.severity}`)}
              />
              {bannerText(banner.message, language)}
            </span>
            <button
              type="button"
              aria-label={t('maintenanceBanner.dismiss')}
              onClick={() => setDismissed(dismissBanner(banner._id))}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </>
  );
}
